import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
    McpActionParam,
    McpActionRegistryService,
    McpRegisteredAction,
} from './mcp-action-registry.service';
import { McpTraceService } from './mcp-trace.service';

export interface PageAction {
    action: string;
    params?: Record<string, any>;
}

export interface PageActionResult {
    action: string;
    data: Record<string, any>;
}

export interface McpLocalKeyword {
    pattern: RegExp;
    action: string;
    params?: Record<string, any>;
    label: string;
}

export interface McpPageAction {
    action: string;
    description: string;
    params?: McpActionParam[];
    handler: (params?: Record<string, any>) => void | boolean | Promise<any>;
    available?: () => boolean;
}

export interface McpPageManifest {
    route: string;
    pageLabel: string;
    actions: McpPageAction[];
    localKeywords?: McpLocalKeyword[];
    getDataSummary?: () => string;
}

@Injectable({ providedIn: 'root' })
export class PageActionService {
    private readonly manifests = new Map<string, McpPageManifest>();
    private readonly _manifest = new BehaviorSubject<McpPageManifest | null>(
        null
    );
    private readonly _action = new Subject<PageAction>();
    private readonly _result = new Subject<PageActionResult>();
    private readonly _focus = new BehaviorSubject<any>(null);

    private static readonly NAV_MAP: Record<string, string> = {
        'orders':       '/orders/list',
        'order':        '/orders/list',
        'order list':   '/orders/list',
        'shipping':     '/shipping',
        'ship':         '/shipping',
        'receiving':    '/receiving',
        'receive':      '/receiving',
        'purchasing':   '/purchasing',
        'purchase':     '/purchasing',
        'artwork':      '/artwork',
        'art':          '/artwork',
        'film':         '/artwork',
        'printer view': '/printer-view',
        'printer':      '/printer-view',
        'resources':    '/resources',
        'resource':     '/resources',
        'dashboard':    '/dashboard',
        'operations':   '/dashboard',
        'ops':          '/dashboard',
        'analytics':    '/analytics',
        'production-plan': '/production-plan',
        'assignment-rules': '/assignment-rules',
        'assignment rules': '/assignment-rules',
        'delivery':     '/delivery',
        'deliveries':   '/delivery',
        'mailbox':      '/mailbox',
        'mail':         '/mailbox',
        'email':        '/mailbox',
        'chat':         '/chat',
        'products':     '/products',
        'crm':          '/crm',
    };
    private static readonly ACTION_ALIASES: Record<string, string[]> = {
        mark_shipped: ['mark_shipping_shipped'],
        mark_shipping_shipped: ['mark_shipped'],
        mark_as_shipped: ['mark_shipping_shipped', 'mark_shipped'],
        ship_it: ['mark_shipping_shipped', 'mark_shipped'],
        ship_order: ['mark_shipping_shipped', 'mark_shipped'],
        ship_this_order: ['mark_shipping_shipped', 'mark_shipped'],
        filter_status: ['set_filter'],
        filter_received: ['set_filter'],
        filter_received_status: ['set_filter'],
        filter_shipped_status: ['set_filter'],
        search_orders: ['find_orders'],
        list_orders: ['find_orders'],
        find_matching_orders: ['find_orders'],
        check_orders: ['find_orders'],
        find_order: ['find_orders'],
        print_and_receive: ['receive_with_labels'],
        receive_and_print: ['receive_with_labels'],
        print_receiving_label: ['receive_with_labels'],
        receive: ['receive_with_labels'],
        print_label: ['print_shipping_label', 'print_first_generated_label'],
        print_shipping: ['print_shipping_label', 'print_first_generated_label'],
        print_label_for_box: ['print_shipping_label'],
        print_box_label: ['print_shipping_label'],
        set_and_save_box: ['create_label'],
        set_box_and_save: ['create_label'],
        save_box: ['create_label'],
        close_order_drawer: ['close_drawer'],
        close_receiving_order: ['close_drawer'],
        close_shipping_order: ['close_drawer'],
        set_tomorrow: ['show_tomorrow'],
        to_tomorrow: ['show_tomorrow'],
        tomorrow_tab: ['show_tomorrow'],
        set_today: ['show_today'],
        to_due_today: ['show_today'],
        today_tab: ['show_today'],
        completion: ['open_completion_dialog', 'open_completion_drawer'],
        open_completion: ['open_completion_dialog', 'open_completion_drawer'],
        open_completion_drawer: ['open_completion_dialog'],
        close_completion: ['close_completion_dialog', 'close_completion_drawer'],
        close_completion_drawer: ['close_completion_dialog'],
        related_orders: ['open_related_orders_drawer'],
        open_related_orders: ['open_related_orders_drawer'],
        view_related_orders: ['open_related_orders_drawer'],
        close_related_orders: ['close_related_orders_drawer'],
        next_related_orders_page: ['next_page'],
        previous_related_orders_page: ['previous_page'],
        edit_order: ['open_edit_order_drawer'],
        open_edit_order: ['open_edit_order_drawer'],
        new_order: ['open_new_order_drawer'],
        start_new_order: ['open_new_order_drawer'],
        new_order_for_customer: ['open_new_order_for_customer'],
        create_customer: ['open_add_customer_drawer'],
        add_customer: ['open_add_customer_drawer'],
        set_order_field: ['set_order_form_field'],
        set_order_fields: ['set_order_form_fields'],
        set_order_basics: ['set_order_basics'],
        order_tab: ['open_order_tab'],
        open_order_tab: ['open_order_tab'],
        go_to_tab: ['open_order_tab'],
        next_order_tab: ['next_order_tab'],
        previous_order_tab: ['previous_order_tab'],
        set_order_name: ['set_order_form_field'],
        set_order_date: ['set_order_form_field'],
        set_order_type: ['set_order_form_field'],
        save_order: ['save_order_form'],
    };

    constructor(
        private readonly registry: McpActionRegistryService,
        private readonly traceService: McpTraceService,
        private readonly router: Router,
    ) {}

    get manifest$(): Observable<McpPageManifest | null> {
        return this._manifest.asObservable();
    }

    getActiveManifest(): McpPageManifest | null {
        return this._manifest.value;
    }

    get action$(): Observable<PageAction> {
        return this._action.asObservable();
    }

    get result$(): Observable<PageActionResult> {
        return this._result.asObservable();
    }

    get focus$(): Observable<any> {
        return this._focus.asObservable();
    }

    registerPage(manifest: McpPageManifest): void {
        this.manifests.set(manifest.route, manifest);
        this._manifest.next(manifest);
    }

    unregisterPage(route: string): void {
        this.manifests.delete(route);
        const current = this._manifest.value;
        if (current?.route === route) {
            this._manifest.next(null);
        }
    }

    setActiveRoute(route: string): void {
        const direct = this.manifests.get(route);
        if (direct) {
            this._manifest.next(direct);
            return;
        }

        // Route can include query/hash; match by normalized path fallback.
        const normalized = this.normalizeActionKey(route);
        const fallback =
            [...this.manifests.entries()].find(([key]) => this.normalizeActionKey(key) === normalized)?.[1] ??
            null;
        this._manifest.next(fallback);
    }

    async dispatch(action: string, params?: Record<string, any>): Promise<boolean> {
        const requested = (action ?? '').trim();
        const candidates = this.getActionCandidates(requested);
        const normalizedCandidates = new Set(candidates.map((name) => this.normalizeActionKey(name)));
        const manifest = this._manifest.value;
        const manual =
            manifest?.actions.find((a) => candidates.includes(a.action)) ??
            manifest?.actions.find(
                (a) =>
                    normalizedCandidates.has(this.normalizeActionKey(a.action))
            );

        if (manual && (!manual.available || manual.available())) {
            const result = manual.handler(params);
            if (result instanceof Promise) {
                const t0 = Date.now();
                this.traceService.log('dispatch_async_start', { action: requested, params },
                    { detail: `Awaiting async action "${requested}"…` });
                const resolved = await result;
                const ms = Date.now() - t0;
                this.traceService.log('dispatch_async_done', { action: requested, ms },
                    { detail: `Async action "${requested}" resolved in ${ms}ms` });
                return resolved !== false;
            }
            return result !== false;
        }

        for (const candidate of candidates) {
            if (this.registry.execute(candidate, params)) {
                return true;
            }
        }

        const registryAlias = this.registry
            .getSnapshot()
            .find(
                (a) =>
                    normalizedCandidates.has(this.normalizeActionKey(a.action))
            );
        if (registryAlias && this.registry.execute(registryAlias.action, params)) {
            return true;
        }

        if (requested === 'navigate' || requested === 'go_to_page') {
            const page = (params?.page ?? '').trim().toLowerCase();
            const route = PageActionService.NAV_MAP[page];
            if (route) {
                this.traceService.log('navigate', { page, route },
                    { detail: `Navigating to ${route}` });
                await this.router.navigateByUrl(route);
                await new Promise(r => setTimeout(r, 300));
                return true;
            }
        }

        this._action.next({ action: requested, params });
        return false;
    }

    hasAction(action: string): boolean {
        const requested = (action ?? '').trim();
        const candidates = this.getActionCandidates(requested);
        const normalizedCandidates = new Set(candidates.map((name) => this.normalizeActionKey(name)));
        const manifest = this._manifest.value;
        const manual =
            manifest?.actions.some(
                (a) =>
                    candidates.includes(a.action) ||
                    normalizedCandidates.has(this.normalizeActionKey(a.action))
            ) ?? false;
        if (manual) {
            return true;
        }

        return this.registry
            .getSnapshot()
            .some(
                (a) =>
                    candidates.includes(a.action) ||
                    normalizedCandidates.has(this.normalizeActionKey(a.action))
            );
    }

    setFocus(entity: any): void {
        this._focus.next(entity);
    }

    clearFocus(): void {
        this._focus.next(null);
    }

    reportResult(action: string, data: Record<string, any>): void {
        this._result.next({ action, data });
    }

    getAvailableActions(
        includeRegistryActions: McpRegisteredAction[] = []
    ): Array<McpPageAction | McpRegisteredAction> {
        const manual = this._manifest.value?.actions ?? [];
        return [...manual, ...includeRegistryActions];
    }

    private normalizeActionKey(value: string): string {
        return String(value ?? '')
            .toLowerCase()
            .split(/[?#]/, 1)[0]
            .trim()
            .replace(/[\s-]+/g, '_')
            .replace(/[^a-z0-9_]/g, '');
    }

    private getActionCandidates(action: string): string[] {
        const requested = (action ?? '').trim();
        const normalized = this.normalizeActionKey(requested);
        const aliasGroup = PageActionService.ACTION_ALIASES[normalized] ?? [];
        const candidates = [requested, normalized, ...aliasGroup];
        return Array.from(new Set(candidates.filter(Boolean)));
    }
}
